// @flow

import * as React from 'react';
import connect from '@active/awe-ui-core/lib/hoc/connect';
import { bindActionCreators } from 'redux';
import { Route, Redirect, Switch } from 'react-router-dom';
import type {
  Match, History, L10n, Event as EventType, PortalPath,
} from 'type/type';
import PageBody from 'components/PageBody/PageBody';
import PageSpinner from 'components/PageSpinner/PageSpinner';
import { clearWhiteLabel } from 'modules/whiteLabel/duck';
import { isWhiteLabelReady } from 'modules/whiteLabel/selector';
import injectL10n from 'react-aaui/lib/shared/injectL10n';
import Maybe, { maybe } from '@active/awe-ui-core/lib/fp/Maybe';
import prop from 'lodash/fp/prop';
import {
  PORTAL_PATH,
  EVENT_PATH,
  EVENT_PARTICIPANTS_PATH,
  IMPORT_PATH,
  EVENT_RESUBMIT_RESULT_PATH,
} from 'services/router/enum';
import appendPortalPath from 'services/router/appendPortalPath';
import { getPortalPath } from 'modules/app/selector';

import HeaderBar from './HeaderBar/HeaderBar';
import Header from './Header/Header';
import Home from './Home/Home';
import SearchParticipants from './SearchParticipants/SearchParticipants';
import ResubmitResult from './ResubmitResult/ResubmitResult';
import {
  isLoadingEvent,
  getApplicationName,
  getEvent,
  getEventViewType,
} from './duck/selector';
import { mount, unmount } from './duck/duck';
import './duck/epics';

type Props = {
  match: Match,
  history: History,
  keyword: string,
  mount: (applicationName: string) => void,
  loading: boolean,
  clearWhiteLabel: () => void,
  unmount: () => void,
  windowLocationOrigin: string,
  event?: ?EventType,
  l10n: L10n,
  portalPath: PortalPath,
  location: Object,
  eventViewType: 'VIRTUAL' | 'PHYSICAL',
}

export class Event extends React.Component<Props> {
  componentDidMount() {
    this.props.mount(this.props.match.params.applicationName);
  }

  componentWillUnmount() {
    this.props.clearWhiteLabel();
    this.props.unmount();
  }

  render() {
    const {
      loading,
      windowLocationOrigin,
      l10n: { formatMessage },
      event,
      portalPath,
      eventViewType,
    } = this.props;
    const { applicationName } = this.props.match.params;
    const evt = Maybe.of(event);
    const eventName = maybe(null, prop('name'), evt);
    const sharingPath = appendPortalPath(portalPath, `/events/${applicationName}`);
    const sharingHref = `${windowLocationOrigin}${sharingPath}`;
    const sharingText = `${formatMessage('global.social.share.view.event', [ eventName ])}`;
    const emailSubject = `${formatMessage('global.social.share.email.subject', [ eventName ])}`;
    const currentTime = new Date().getTime();
    const isVirtual = eventViewType === 'VIRTUAL';
    const endDate = maybe(null, prop('endDate'), evt);
    const isEventHomePage = /^\/events\/.((?!\/(participants|import)).)*$/.test(window.location.pathname);
    const expandContent = isEventHomePage && isVirtual && currentTime <= new Date(endDate).getTime();

    return (
      loading
        ? <PageSpinner />
        : <React.Fragment>
          <Header />
          <HeaderBar
            sharingHref={sharingHref}
            sharingText={sharingText}
            emailSubject={emailSubject}
            event={event}
            applicationName={applicationName} />
          <PageBody expandContent={expandContent}>
            <Switch>
              <Route path={`${PORTAL_PATH}${EVENT_PATH}`} exact component={Home} />
              <Route path={`${PORTAL_PATH}${EVENT_PARTICIPANTS_PATH}`} component={SearchParticipants} />
              <Route path={EVENT_RESUBMIT_RESULT_PATH} component={ResubmitResult} />
              <Redirect from={IMPORT_PATH} to={`/manage-results/${applicationName}/edit`} />
            </Switch>
          </PageBody>
        </React.Fragment>
    );
  }
}

const mapState = (state) => {
  return {
    loading: isLoadingEvent(state) || !getApplicationName(state) || !isWhiteLabelReady(state),
    windowLocationOrigin: window.location.origin,
    event: getEvent(state),
    portalPath: getPortalPath(state),
    eventViewType: getEventViewType(state),
  };
};

const mapDispatch = dispatch => bindActionCreators({
  mount,
  unmount,
  clearWhiteLabel,
}, dispatch);

const EventContainer = connect(mapState, mapDispatch)(Event);

export default injectL10n()(EventContainer);
